import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/authStore";
import { Button } from "@learn-me/ui";
import { Menu, X, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "@/hooks";
import { ANIMATION_CONFIG } from "@/lib/animations";
import { Logo } from "@/components/Logo";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";

/**
 * Navegación agrupada en cuatro entradas: las dos líneas de negocio con sus
 * páginas, más quiénes somos y contacto.
 */
const NAV_GROUPS: Array<{
  label: string;
  to?: string;
  items?: Array<{ label: string; to: string; description: string }>;
}> = [
  {
    label: "Para empresas",
    items: [
      {
        label: "Soluciones",
        to: "/empresas",
        description: "Plataformas y rutas formativas a la medida",
      },
      {
        label: "Programas y capacitaciones",
        to: "/programas",
        description: "Formación especializada para organizaciones",
      },
    ],
  },
  {
    label: "Aprendizaje en línea",
    items: [
      {
        label: "Cursos",
        to: "/courses",
        description: "Oferta digital autogestionada",
      },
      {
        label: "Materiales",
        to: "/materiales",
        description: "Recursos prácticos descargables",
      },
    ],
  },
  { label: "Quiénes somos", to: "/quienes-somos" },
  { label: "Contacto", to: "/contacto" },
];

function NavDropdown({
  label,
  items,
}: {
  label: string;
  items: Array<{ label: string; to: string; description: string }>;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Se cierra al hacer clic fuera o con Escape
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full pt-2 w-72">
          <div className="rounded-xl border bg-white shadow-lg p-2">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2.5 hover:bg-secondary-50 transition-colors"
              >
                <span className="block font-medium text-secondary-900">
                  {item.label}
                </span>
                <span className="block text-sm text-muted-foreground">
                  {item.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicLayout() {
  const { isAuthenticated, user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b">
        <nav className="container flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo className="h-10" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {NAV_GROUPS.map((group) =>
              group.items ? (
                <NavDropdown
                  key={group.label}
                  label={group.label}
                  items={group.items}
                />
              ) : (
                <Link
                  key={group.label}
                  to={group.to!}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {group.label}
                </Link>
              ),
            )}
            {isAuthenticated ? (
              <>
                <Link to={user?.role === "ADMIN" ? "/admin" : "/dashboard"}>
                  <Button variant="outline">
                    {user?.role === "ADMIN" ? "Panel Admin" : "Mi Dashboard"}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost">Iniciar Sesión</Button>
                </Link>
                <Link to="/register">
                  <Button>Registrarse</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={prefersReducedMotion ? {} : { opacity: 0, height: 0 }}
              transition={{ duration: ANIMATION_CONFIG.duration.fast }}
              className="md:hidden border-t bg-white overflow-hidden"
            >
              <div className="container py-4 space-y-4">
                {NAV_GROUPS.map((group) =>
                  group.items ? (
                    <div key={group.label} className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-400">
                        {group.label}
                      </p>
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="block pl-3 text-muted-foreground hover:text-foreground"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={group.label}
                      to={group.to!}
                      className="block text-muted-foreground hover:text-foreground"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {group.label}
                    </Link>
                  ),
                )}
                {isAuthenticated ? (
                  <Link
                    to={user?.role === "ADMIN" ? "/admin" : "/dashboard"}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full">
                      {user?.role === "ADMIN" ? "Panel Admin" : "Mi Dashboard"}
                    </Button>
                  </Link>
                ) : (
                  <div className="space-y-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">
                        Iniciar Sesión
                      </Button>
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button className="w-full">Registrarse</Button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? {} : { opacity: 0, y: -8 }}
            transition={{ duration: ANIMATION_CONFIG.duration.normal }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/50">
        <div className="container py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="flex items-center mb-4">
                <Logo className="h-8" />
              </Link>
              <p className="text-sm text-muted-foreground">
                Tecnología y formación que se adaptan a su organización.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/quienes-somos" className="hover:text-foreground">
                    Quiénes somos
                  </Link>
                </li>
                <li>
                  <Link to="/empresas" className="hover:text-foreground">
                    Soluciones para empresas
                  </Link>
                </li>
                <li>
                  <Link to="/programas" className="hover:text-foreground">
                    Programas y capacitaciones
                  </Link>
                </li>
                <li>
                  <Link to="/contacto" className="hover:text-foreground">
                    Contacto
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Cursos</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/courses" className="hover:text-foreground">
                    Todos los cursos
                  </Link>
                </li>
                <li>
                  <Link
                    to="/courses?isFree=true"
                    className="hover:text-foreground"
                  >
                    Cursos gratis
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a
                    href="mailto:admin@arcuslearn.com"
                    className="hover:text-foreground"
                  >
                    Centro de ayuda
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:admin@arcuslearn.com"
                    className="hover:text-foreground"
                  >
                    Contacto
                  </a>
                </li>
                <li>
                  <a
                    href="https://wa.me/50688195502"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground"
                  >
                    WhatsApp +506 8819 5502
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/terminos" className="hover:text-foreground">
                    Términos y Condiciones
                  </Link>
                </li>
                <li>
                  <Link to="/privacidad" className="hover:text-foreground">
                    Política de Privacidad
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>
              &copy; {new Date().getFullYear()} ARCUS Learning. Todos los
              derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      <WhatsAppFloatingButton />
    </div>
  );
}
